import { siteDashboardUrl } from '@/config/siteConfig'

/**
 * Carrying the console's language to the sites it links out to.
 *
 * The console and the dashboards are on different hosts, so the language each
 * one remembers lives in its own localStorage and neither can read the other's.
 * Changing the language in Settings therefore did nothing to them: a dashboard
 * opened in whatever it had saved, or in its own first-run default.
 *
 * Putting the choice in the URL is what crosses that boundary. The dashboard
 * reads `?lang` on load and saves it as its own preference, so the hand-off
 * happens once and the dashboard keeps it from then on.
 *
 * Deliberately takes the address rather than owning one: which dashboard the
 * console points at differs per deployment, and a default here would be wrong
 * on every branch but the one it was written for.
 */

/**
 * `url` with the console's current language attached.
 *
 * The console and the dashboards use the same language codes, so the value is
 * passed through as it is; the far side decides what to do with one it does not
 * have. A relative or malformed address is returned untouched.
 */
export function withLanguage(url: string, language: string | undefined): string {
  if (!url || !language) return url

  try {
    const parsed = new URL(url)
    parsed.searchParams.set('lang', language)
    return parsed.toString()
  } catch {
    // Not an absolute URL: an in-app route needs no hand-off, since it is the
    // same origin and already shares the console's stored language.
    return url
  }
}

// Address of the dashboard this console links to.
//
// The site's run-time setting (CONSOLE_DASHBOARD_URL on the container, see
// src/config/siteConfig.ts) wins. Every site is named the same way: console.<domain> and dashboard.<domain>
// share the domain and the session cookie. Swapping the first label of this
// host is therefore right on an on-prem site out of the box, with no setting
// to get wrong. VITE_DASHBOARD_URL (a build argument) wins for the deployments
// that do not follow the convention, such as a shared UAT host where each
// tenant has its own dashboard-<tenant>.<domain>.
const FALLBACK_DASHBOARD_URL = 'https://dashboard-bri.uat.synstream.co/'

export function dashboardBaseUrl(): string {
  const configured = siteDashboardUrl() || ((import.meta.env.VITE_DASHBOARD_URL as string) || '').trim()
  if (configured) return configured.replace(/\/+$/, '') + '/'
  if (typeof window === 'undefined') return FALLBACK_DASHBOARD_URL
  const { protocol, hostname, port } = window.location
  const labels = hostname.split('.')
  // localhost or a bare IP: nothing to derive from, keep the development target
  if (labels.length < 3 || /^\d+$/.test(labels[labels.length - 1])) return FALLBACK_DASHBOARD_URL
  return `${protocol}//${['dashboard', ...labels.slice(1)].join('.')}${port ? `:${port}` : ''}/`
}

/** The dashboard address with the console's current language attached. */
export function dashboardUrl(language: string | undefined): string {
  return withLanguage(dashboardBaseUrl(), language)
}
